import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

type ThemeContextValue = {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'pdm.theme';

function readStoredMode(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'system';
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyTheme(mode: ThemeMode): ResolvedTheme {
  const resolved: ResolvedTheme =
    mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode;
  const root = document.documentElement;
  if (mode === 'system') {
    // Remove the override so the @media (prefers-color-scheme: dark)
    // branch in dark-theme.css takes precedence.
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', mode);
  }
  return resolved;
}

/**
 * Theme provider. Three modes: 'system' (default; follows OS), 'light',
 * 'dark'. User preference persists in localStorage so the choice
 * survives reloads.
 *
 * The actual CSS variable swap lives in styles/dark-theme.css — this
 * component just toggles the html[data-theme] attribute and exposes
 * the resolved theme so screens can opt into per-mode behavior (e.g.,
 * chart strategies that paint differently in dark mode).
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => {
    if (typeof document === 'undefined') return 'light';
    return applyTheme(readStoredMode());
  });

  useEffect(() => {
    setResolved(applyTheme(mode));
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, mode);
    }
  }, [mode]);

  // Track OS preference changes so 'system' mode stays in sync.
  useEffect(() => {
    if (mode !== 'system' || typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(systemPrefersDark() ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolved, setMode: setModeState }),
    [mode, resolved],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
